import {
  ActionExecutionEventFragment,
  ActionExecutionEventType,
  ActionExecutionFragment,
  ActionExecutionNoChildrenFragment,
  TaskListItemFragment,
} from '@shared/generated/graphql';
import {
  ActionExecution,
  ActionExecutionEvent,
  ActionExecutionEventData,
  TaskListItem,
} from '@shared/types/action-execution';
import { assertUnreachable } from 'clerk_common/enum';
import { isNil } from 'lodash';
import {
  actionDefinitionPreviewFromFragment,
  createName,
} from './actionDefinition';
import { conversationFromFragment } from './conversation';
import { userFromFragment } from './user';

export function actionExecutionFromFragment(
  fragment: ActionExecutionFragment
): ActionExecution {
  return {
    ...actionExecutionNoChildrenFromFragment(fragment),
    children: fragment.children.map(actionExecutionNoChildrenFromFragment),
    events: fragment.events.map(actionExecutionEventFromFragment),
  };
}

export function taskListItemFromFragment(
  fragment: TaskListItemFragment
): TaskListItem {
  return {
    id: fragment.id,
    createdAt: new Date(fragment.createdAt),
    deletedAt: fragment.deletedAt ? new Date(fragment.deletedAt) : null,
    status: fragment.status,
    autonomyState: fragment.autonomyState,
    tags: fragment.tags ?? [],
    actionDefinitionName: createName(
      fragment.actionDefinition.name,
      fragment.actionDefinition.deletedAt
    ),
  };
}

function actionExecutionNoChildrenFromFragment(
  fragment: ActionExecutionNoChildrenFragment
): ActionExecution {
  return {
    id: fragment.id,
    createdAt: new Date(fragment.createdAt),
    updatedAt: new Date(fragment.updatedAt),
    deletedAt: fragment.deletedAt ? new Date(fragment.deletedAt) : null,
    status: fragment.status,
    autonomyState: fragment.autonomyState,
    executeAsUser: fragment.executeAsUser
      ? userFromFragment(fragment.executeAsUser)
      : undefined,
    executeAsUserId: fragment.executeAsUserId ?? undefined,
    actionDefinitionPreview: actionDefinitionPreviewFromFragment(
      fragment.actionDefinition
    ),
    children: [],
    conversation: fragment.conversation
      ? conversationFromFragment({
          ...fragment.conversation,
          events: [],
          ratings: [],
          analyses: [],
        })
      : undefined,
    linkedConversations: fragment.linkedConversations.map((conversation) =>
      conversationFromFragment({
        ...conversation,
        events: [],
        ratings: [],
        analyses: [],
      })
    ),
    tags: fragment.tags ?? [],
    mode: fragment.mode,
    events: fragment.events.map(actionExecutionEventFromFragment),
    documents: fragment.documents.map((document) => ({
      id: document.id,
      createdAt: new Date(document.createdAt),
      name: document.name ?? undefined,
      url: document.url,
    })),
    input: fragment.input,
    output: fragment.output,
    parentId: fragment.parentId ?? undefined,
  };
}

export function actionExecutionEventFromFragment(
  fragment: ActionExecutionEventFragment
): ActionExecutionEvent {
  return {
    id: fragment.id,
    createdAt: new Date(fragment.createdAt),
    name: fragment.name,
    agentMessageView: fragment.agentMessageView,
    data: eventDataFromFragment(fragment.data),
  };
}

function eventDataFromFragment(
  data: ActionExecutionEventFragment['data']
): ActionExecutionEventData | undefined {
  const variantName = data?.__typename;
  if (isNil(data) || isNil(variantName)) return undefined;
  switch (variantName) {
    case 'EscalationData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.ESCALATION,
        escalationRationale: data.escalationRationale ?? undefined,
      };
    case 'DecisionEdgeEvaluatedData':
      return {
        ...data,
        targetNodeActionDefinitionId:
          data.targetNodeActionDefinitionId ?? undefined,
        eventDataType: ActionExecutionEventType.DECISION_EDGE_EVALUATED,
      };
    case 'ToolUsedData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.TOOL_USED,
      };
    case 'SupervisorToolUsedData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.SUPERVISOR_TOOL_USED,
      };
    case 'RawAgentMessageData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.RAW_AGENT_MESSAGE,
      };
    case 'RawSupervisorMessageData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.RAW_SUPERVISOR_MESSAGE,
      };
    case 'AgentReasoningData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.AGENT_REASONING,
      };
    case 'SupervisorReasoningData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.SUPERVISOR_REASONING,
      };
    case 'MemoryRecalledData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.MEMORY_RECALLED,
      };
    case 'UnknownData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.UNKNOWN,
      };
    case 'SpamGuardData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.SPAM_GUARD,
      };
    case 'ReminderData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.REMINDER,
        message: data.message,
      };
    case 'InstructionMessageData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.INSTRUCTION_MESSAGE,
      };
    case 'StatusChangedData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.STATUS_CHANGED,
      };
    case 'AgentProposedOutputData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.AGENT_PROPOSED_OUTPUT,
        output: data.output,
      };
    case 'SupervisorProposedOutputData':
      return {
        ...data,
        eventDataType: ActionExecutionEventType.SUPERVISOR_PROPOSED_OUTPUT,
        output: data.output,
      };
    case 'AeWatcherClosedData':
      return {
        eventDataType: ActionExecutionEventType.WATCHER_CLOSED,
      };
    case 'WatcherPokedData':
      return {
        eventDataType: ActionExecutionEventType.WATCHER_POKED,
      };
    default:
      return assertUnreachable(variantName);
  }
}
